import React, { useState } from 'react';
import { Palette, RotateCcw, Sun, Type } from 'lucide-react';
import {
  BUILTIN_COLORS,
  COLOR_ROLES,
  FONT_OPTIONS,
  TEXT_ROLES,
  TYPE_SCALE_MAX,
  TYPE_SCALE_MIN,
  fontStack,
  parseHex,
  type ThemeColorRole,
  type ThemeFontId,
  type ThemeTextRole,
} from '../../theme/appearance';
import { useAppearance } from '../../theme/AppearanceContext';
import { useTheme } from '../../theme/ThemeContext';
import type { MessageKey } from '../../i18n/en';
import { useLanguage, type Translate } from '../../i18n/LanguageContext';
import './settings.css';

const TEXT_ROWS: ReadonlyArray<{
  id: ThemeTextRole;
  titleKey: MessageKey;
  sampleKey: MessageKey;
  whereKey: MessageKey;
  changesKey: MessageKey;
}> = [
  {
    id: 'header',
    titleKey: 'theme.text.header.title',
    sampleKey: 'theme.text.header.sample',
    whereKey: 'theme.text.header.where',
    changesKey: 'theme.text.header.changes',
  },
  {
    id: 'bold',
    titleKey: 'theme.text.bold.title',
    sampleKey: 'theme.text.bold.sample',
    whereKey: 'theme.text.bold.where',
    changesKey: 'theme.text.bold.changes',
  },
  {
    id: 'normal',
    titleKey: 'theme.text.normal.title',
    sampleKey: 'theme.text.normal.sample',
    whereKey: 'theme.text.normal.where',
    changesKey: 'theme.text.normal.changes',
  },
  {
    id: 'descriptive',
    titleKey: 'theme.text.descriptive.title',
    sampleKey: 'theme.text.descriptive.sample',
    whereKey: 'theme.text.descriptive.where',
    changesKey: 'theme.text.descriptive.changes',
  },
  {
    id: 'other',
    titleKey: 'theme.text.other.title',
    sampleKey: 'theme.text.other.sample',
    whereKey: 'theme.text.other.where',
    changesKey: 'theme.text.other.changes',
  },
];

const COLOR_ROWS: ReadonlyArray<{
  id: ThemeColorRole;
  titleKey: MessageKey;
  whereKey: MessageKey;
  changesKey: MessageKey;
}> = [
  {
    id: 'primary',
    titleKey: 'theme.color.primary.title',
    whereKey: 'theme.color.primary.where',
    changesKey: 'theme.color.primary.changes',
  },
  {
    id: 'accent',
    titleKey: 'theme.color.accent.title',
    whereKey: 'theme.color.accent.where',
    changesKey: 'theme.color.accent.changes',
  },
  {
    id: 'info',
    titleKey: 'theme.color.info.title',
    whereKey: 'theme.color.info.where',
    changesKey: 'theme.color.info.changes',
  },
  {
    id: 'success',
    titleKey: 'theme.color.success.title',
    whereKey: 'theme.color.success.where',
    changesKey: 'theme.color.success.changes',
  },
  {
    id: 'warning',
    titleKey: 'theme.color.warning.title',
    whereKey: 'theme.color.warning.where',
    changesKey: 'theme.color.warning.changes',
  },
  {
    id: 'danger',
    titleKey: 'theme.color.danger.title',
    whereKey: 'theme.color.danger.where',
    changesKey: 'theme.color.danger.changes',
  },
];

const FONT_WEIGHTS: ReadonlyArray<{ weight: number; labelKey: MessageKey }> = [
  { weight: 400, labelKey: 'theme.weight.regular' },
  { weight: 500, labelKey: 'theme.weight.medium' },
  { weight: 600, labelKey: 'theme.weight.semibold' },
  { weight: 700, labelKey: 'theme.weight.bold' },
  { weight: 800, labelKey: 'theme.weight.extrabold' },
  { weight: 900, labelKey: 'theme.weight.black' },
];

const FONT_NOTES: Record<ThemeFontId, MessageKey> = {
  inter: 'theme.font.inter.note',
  'google-sans': 'theme.font.google-sans.note',
  helvetica: 'theme.font.helvetica.note',
  arial: 'theme.font.arial.note',
};

function faceLabel(t: Translate, font: ThemeFontId, weight: number): string {
  if (font === 'google-sans') return weight <= 500 ? t('theme.face.gsRegular') : t('theme.face.gsBold');
  if (font === 'arial') {
    if (weight === 400) return t('theme.face.arialRegular');
    if (weight >= 700) return t('theme.face.arialBold');
    return t('theme.face.arialSynth');
  }
  if (font === 'helvetica') {
    return weight === 400 ? t('theme.face.helveticaRegular') : t('theme.face.helveticaOther');
  }
  return t('theme.face.inter', { weight });
}

function percentOf(scale: number): number {
  return Math.round(scale * 100);
}

const SettingsView: React.FC = () => {
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { appearance, setFont, setTypeScale, setColor, resetText, resetColors, resetFont, resetAll } =
    useAppearance();
  const [hexDraft, setHexDraft] = useState<Partial<Record<ThemeColorRole, string>>>({});
  const textDefault = TEXT_ROLES.every((role) => appearance.typeScale[role] === 1);
  const colorDefault = COLOR_ROLES.every((role) => appearance.colors[role] == null);
  const fontDefault = appearance.font === 'inter';
  const allDefault = textDefault && colorDefault && fontDefault && theme === 'dark';

  const resetEverything = () => {
    resetAll();
    setTheme('dark');
    setHexDraft({});
  };

  return (
    <div className="tm">
      <section className="tm-card" aria-label={t('theme.manager')}>
        <div className="tm-toolbar">
          <div>
            <h2>{t('theme.manager')}</h2>
            <p className="tm-lead">{t('theme.lead')}</p>
          </div>
          <button type="button" className="tm-btn" onClick={resetEverything} disabled={allDefault}>
            <RotateCcw size={14} aria-hidden="true" /> {t('theme.resetAll')}
          </button>
        </div>
        <div className="tm-preview">
          <div>
            <p className="tm-preview__kicker">{t('theme.previewKicker')}</p>
            <p className="tm-preview__title">{t('theme.previewTitle')}</p>
            <p className="tm-preview__bold">{t('theme.previewBold')}</p>
            <p className="tm-preview__body">{t('theme.previewBody')}</p>
            <p className="tm-preview__help">{t('theme.previewHelp')}</p>
          </div>
          <div className="tm-pills" aria-hidden="true">
            <span className="tm-pill tm-pill--primary">{t('theme.color.primary.title')}</span>
            <span className="tm-pill tm-pill--accent">{t('theme.color.accent.title')}</span>
            <span className="tm-pill tm-pill--info">{t('theme.color.info.title')}</span>
            <span className="tm-pill tm-pill--success">{t('theme.color.success.title')}</span>
            <span className="tm-pill tm-pill--warning">{t('theme.color.warning.title')}</span>
            <span className="tm-pill tm-pill--danger">{t('theme.color.danger.title')}</span>
          </div>
        </div>
      </section>

      <section className="tm-card" aria-labelledby="tm-text-title">
        <div className="tm-section__head">
          <div>
            <h2 id="tm-text-title">
              <Type size={18} aria-hidden="true" /> {t('theme.text.title')}
            </h2>
            <p className="tm-lead">{t('theme.text.lead')}</p>
          </div>
          <button type="button" className="tm-btn" onClick={resetText} disabled={textDefault}>
            {t('theme.resetText')}
          </button>
        </div>
        <div className="tm-list">
          {TEXT_ROWS.map((row) => {
            const scale = appearance.typeScale[row.id];
            const percent = percentOf(scale);
            const fill = ((percent - 75) / 75) * 100;
            const title = t(row.titleKey);
            return (
              <article key={row.id} className="tm-role">
                <div className="tm-sample-box" aria-hidden="true">
                  <p className={`tm-sample tm-sample--${row.id}`}>{t(row.sampleKey)}</p>
                </div>
                <div className="tm-copy">
                  <div className="tm-role__top">
                    <h3>{title}</h3>
                    <div className="tm-control">
                      <span className="tm-percent">{percent}%</span>
                      <input
                        className="tm-range"
                        type="range"
                        min={percentOf(TYPE_SCALE_MIN)}
                        max={percentOf(TYPE_SCALE_MAX)}
                        step={5}
                        value={percent}
                        aria-label={t('theme.sizeAria', { title })}
                        aria-valuetext={`${percent}%`}
                        style={{
                          background: `linear-gradient(90deg, var(--shell-accent) ${fill}%, var(--shell-line) ${fill}%)`,
                        }}
                        onChange={(event) => setTypeScale(row.id, Number(event.target.value) / 100)}
                      />
                      <button
                        type="button"
                        className="tm-btn"
                        onClick={() => setTypeScale(row.id, 1)}
                        disabled={scale === 1}
                      >
                        {t('theme.reset')}
                      </button>
                    </div>
                  </div>
                  <div className="tm-meta">
                    <p className="tm-where">
                      <strong>{t('theme.whereLabel')} </strong>
                      {t(row.whereKey)}
                    </p>
                    <p className="tm-changes">
                      <strong>{t('theme.changesLabel')} </strong>
                      {t(row.changesKey)}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="tm-card" aria-labelledby="tm-color-title">
        <div className="tm-section__head">
          <div>
            <h2 id="tm-color-title">
              <Palette size={18} aria-hidden="true" /> {t('theme.color.title')}
            </h2>
            <p className="tm-lead">{t('theme.color.lead')}</p>
          </div>
          <button type="button" className="tm-btn" onClick={resetColors} disabled={colorDefault}>
            {t('theme.resetColors')}
          </button>
        </div>
        <div className="tm-list">
          {COLOR_ROWS.map((row) => {
            const custom = appearance.colors[row.id];
            const builtin = BUILTIN_COLORS[row.id][theme];
            const shown = custom ?? builtin;
            const draft = hexDraft[row.id];
            const title = t(row.titleKey);
            return (
              <article key={row.id} className="tm-color">
                <span className="tm-swatch" style={{ background: shown }} aria-hidden="true" />
                <div className="tm-copy">
                  <div className="tm-color__top">
                    <h3>{title}</h3>
                    <span className={`tm-pill tm-pill--${row.id}`}>{title}</span>
                    <div className="tm-color-tools">
                      <input
                        className="tm-color-input"
                        type="color"
                        value={shown}
                        aria-label={t('theme.colorAria', { title })}
                        onChange={(event) => {
                          const next = parseHex(event.target.value);
                          if (!next) return;
                          setHexDraft((current) => ({ ...current, [row.id]: next }));
                          setColor(row.id, next);
                        }}
                      />
                      <input
                        className="tm-hex"
                        type="text"
                        inputMode="text"
                        spellCheck={false}
                        autoCapitalize="off"
                        autoCorrect="off"
                        maxLength={7}
                        value={draft ?? custom ?? builtin}
                        aria-label={t('theme.hexAria', { title })}
                        onChange={(event) => {
                          const next = event.target.value;
                          setHexDraft((current) => ({ ...current, [row.id]: next }));
                          const parsed = parseHex(next);
                          if (parsed) setColor(row.id, parsed);
                        }}
                      />
                      <span className="tm-status">{custom ? t('theme.custom') : t('theme.builtin')}</span>
                      <button
                        type="button"
                        className="tm-btn"
                        onClick={() => {
                          setHexDraft((current) => ({ ...current, [row.id]: undefined }));
                          setColor(row.id, null);
                        }}
                        disabled={custom == null}
                      >
                        {t('theme.reset')}
                      </button>
                    </div>
                  </div>
                  <div className="tm-meta">
                    <p className="tm-where">
                      <strong>{t('theme.whereLabel')} </strong>
                      {t(row.whereKey)}
                    </p>
                    <p className="tm-changes">
                      <strong>{t('theme.changesLabel')} </strong>
                      {t(row.changesKey)}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="tm-card" aria-labelledby="tm-theme-title">
        <div className="tm-section__head">
          <div>
            <h2 id="tm-theme-title">
              <Sun size={18} aria-hidden="true" /> {t('theme.mode.title')}
            </h2>
            <p className="tm-lead">{t('theme.mode.lead')}</p>
          </div>
        </div>
        <div className="tm-themes" role="group" aria-label={t('theme.mode.group')}>
          {(['dark', 'light'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              className={`tm-theme${theme === mode ? ' is-active' : ''}`}
              aria-pressed={theme === mode}
              onClick={() => setTheme(mode)}
            >
              <span className={`tm-theme__stage tm-theme__stage--${mode}`} aria-hidden="true">
                <span className="tm-theme__bar" />
                <span className="tm-theme__side" />
                <span className="tm-theme__main">
                  <span className="tm-theme__line tm-theme__line--lg" />
                  <span className="tm-theme__line" />
                  <span
                    className="tm-theme__pill"
                    style={{ background: appearance.colors.primary ?? BUILTIN_COLORS.primary[mode] }}
                  />
                </span>
              </span>
              <span className="tm-theme__name">
                {t(mode === 'dark' ? 'theme.mode.dark' : 'theme.mode.light')}
              </span>
              <span className="tm-theme__hint">
                {t(mode === 'dark' ? 'theme.mode.darkHint' : 'theme.mode.lightHint')}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="tm-card" aria-labelledby="tm-font-title">
        <div className="tm-section__head">
          <div>
            <h2 id="tm-font-title">{t('theme.font.title')}</h2>
            <p className="tm-lead">{t('theme.font.lead')}</p>
          </div>
          <button type="button" className="tm-btn" onClick={resetFont} disabled={fontDefault}>
            {t('theme.resetFont')}
          </button>
        </div>
        <div className="tm-fonts" role="group" aria-label={t('theme.font.group')}>
          {FONT_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`tm-font${appearance.font === option.id ? ' is-active' : ''}`}
              aria-pressed={appearance.font === option.id}
              onClick={() => setFont(option.id)}
            >
              <span className="tm-font__name" style={{ fontFamily: fontStack(option.id) }}>
                {option.label}
              </span>
              <span className="tm-font__sample" style={{ fontFamily: fontStack(option.id) }}>
                {t('theme.font.sample')}
              </span>
              <span className="tm-font__note">{t(FONT_NOTES[option.id])}</span>
            </button>
          ))}
        </div>
        <div className="tm-weights" style={{ fontFamily: fontStack(appearance.font) }}>
          {FONT_WEIGHTS.map((item) => (
            <div key={item.weight} className="tm-weight">
              <span className="tm-weight__label">{t(item.labelKey)}</span>
              <span className="tm-weight__sample" style={{ fontWeight: item.weight }}>
                {t('theme.weight.sample')}
              </span>
              <span className="tm-weight__face">{faceLabel(t, appearance.font, item.weight)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SettingsView;
